import { useRef, useState } from "react";
import { supabase } from "../../../lib/supabaseClient";

interface ImageUploadFieldProps {
  label: string;
  value: string;
  onChange: (url: string) => void;
  placeholder?: string;
  previewClassName?: string;
  previewShape?: "rect" | "circle";
}

const BUCKET = "site-images";

export default function ImageUploadField({
  label,
  value,
  onChange,
  placeholder = "https://example.com/image.jpg",
  previewClassName = "h-20 w-full object-cover rounded-md",
  previewShape = "rect",
}: ImageUploadFieldProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadError(null);

    try {
      const ext = file.name.split(".").pop() ?? "jpg";
      const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

      const { error: uploadErr } = await supabase.storage
        .from(BUCKET)
        .upload(fileName, file, { upsert: false });

      if (uploadErr) throw uploadErr;

      const { data } = supabase.storage.from(BUCKET).getPublicUrl(fileName);
      onChange(data.publicUrl);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "アップロードに失敗しました";
      setUploadError(msg);
    } finally {
      setUploading(false);
    }

    e.target.value = "";
  };

  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>

      {/* URL入力 + アップロードボタン */}
      <div className="flex gap-2 items-center mb-2">
        <input
          type="text"
          value={value}
          onChange={(e) => {
            setUploadError(null);
            onChange(e.target.value);
          }}
          className="flex-1 border border-gray-200 rounded-md px-3 py-2 text-xs focus:outline-none focus:border-pink-400"
          placeholder={placeholder}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-1.5 px-3 py-2 bg-pink-50 border border-pink-200 rounded-md text-xs text-pink-600 hover:bg-pink-100 cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {uploading ? (
            <>
              <i className="ri-loader-4-line animate-spin text-sm" />
              アップロード中...
            </>
          ) : (
            <>
              <i className="ri-upload-2-line text-sm" />
              アップロード
            </>
          )}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
        />
      </div>

      {/* エラー表示 */}
      {uploadError && (
        <p className="text-xs text-red-500 mb-1 flex items-center gap-1">
          <i className="ri-error-warning-line" /> {uploadError}
        </p>
      )}

      {/* プレビュー */}
      {value && (
        <div className="relative group">
          <img
            src={value}
            alt="プレビュー"
            className={
              previewShape === "circle"
                ? "w-16 h-16 object-cover rounded-full border border-gray-200"
                : previewClassName
            }
          />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center bg-black/50 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
          >
            <i className="ri-close-line text-xs" />
          </button>
        </div>
      )}
    </div>
  );
}
